import { useState } from 'react'
import { Box, Typography, Grid, Alert, Paper, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseIcon from '@mui/icons-material/Close'
import FilterPanel from '../../components/FilterPanel'
import EfficiencyTrend from '../../components/charts/EfficiencyTrend'
import CorrelationAnalysis from '../../components/charts/CorrelationAnalysis'
import { useFilters } from '../../context/FilterContext'
import { DataQualityBanner } from '../../components/ui/DataQualityContext'

function TrendsAnalysis() {
  const { filteredData, activeFilterCount } = useFilters()
  const [notesOpen, setNotesOpen] = useState(false)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Trends & Correlation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Efficiency trends over time and variable correlation analysis
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FilterPanel compact />
      </Box>
      <Box sx={{ mb: 3 }}>
        <DataQualityBanner compact />
      </Box>

      {activeFilterCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Showing analysis for {filteredData.length} filtered studies.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Efficiency Trend - Full Width */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, overflow: 'auto' }}>
            <EfficiencyTrend data={filteredData} title="Efficiency Improvement Trend Over Time" />
          </Paper>
        </Grid>

        {/* Correlation Analysis - Full Width */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, overflow: 'auto' }}>
            <CorrelationAnalysis data={filteredData} title="Variable Correlation Explorer" />
          </Paper>
        </Grid>
      </Grid>

      {/* Info Note - Clickable */}
      <Alert
        severity="info"
        icon={false}
        onClick={() => setNotesOpen(true)}
        sx={{
          mt: 4,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Correlation Analysis Notes
            </Typography>
            <Typography variant="body2">
              Pearson correlation coefficient (r) measures linear relationship strength.
              R² indicates the proportion of variance explained. Use the variable selectors to explore
              relationships between equipment age, capacity, efficiency, and savings metrics.
            </Typography>
          </Box>
          <InfoOutlinedIcon sx={{ color: 'info.main', ml: 2, flexShrink: 0 }} />
        </Box>
      </Alert>

      {/* Educational Modal */}
      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Understanding Correlation Analysis
          <IconButton onClick={() => setNotesOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" color="primary" gutterBottom>
            What is Correlation?
          </Typography>
          <Typography variant="body2" paragraph>
            Correlation measures how two variables move together. When one goes up, does the other go up too (positive), go down (negative), or show no pattern (zero correlation)?
          </Typography>

          <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 3 }}>
            The r Value (Correlation Coefficient)
          </Typography>
          <Typography variant="body2" paragraph>
            The Pearson correlation coefficient (r) ranges from -1 to +1:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2"><strong>r = +1.0:</strong> Perfect positive correlation (both variables always move together)</Typography>
            <Typography component="li" variant="body2"><strong>r = 0.0:</strong> No correlation (variables are independent)</Typography>
            <Typography component="li" variant="body2"><strong>r = -1.0:</strong> Perfect negative correlation (when one goes up, the other goes down)</Typography>
          </Box>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Strength guide:</strong></Typography>
            <Typography variant="body2">|r| ≥ 0.7 = Strong | |r| 0.4-0.7 = Moderate | |r| 0.2-0.4 = Weak | |r| &lt; 0.2 = Very Weak</Typography>
          </Box>

          <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 3 }}>
            R² (R-Squared) - Variance Explained
          </Typography>
          <Typography variant="body2" paragraph>
            R² tells you what percentage of one variable's behavior can be explained by the other. For example, R² = 25% means one variable explains 25% of the variation in the other — the remaining 75% is due to other factors.
          </Typography>

          <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 3 }}>
            Why This Matters for PermaFrost NMR
          </Typography>
          <Box sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
            <Typography variant="body2" paragraph>
              <strong>Key Finding:</strong> Equipment age shows very weak correlation with efficiency improvement.
            </Typography>
            <Typography variant="body2" paragraph>
              This is significant because it proves PermaFrost NMR delivers <strong>consistent results regardless of equipment age</strong>. As a chemical treatment, it works predictably every time — whether the equipment is 3 years old or 25 years old.
            </Typography>
            <Typography variant="body2">
              This consistency is a major advantage: facility managers don't need to worry about equipment age when evaluating whether the treatment will be effective. The data proves it works across the board.
            </Typography>
          </Box>

          <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 3 }}>
            How to Use This Tool
          </Typography>
          <Typography variant="body2">
            Use the X and Y axis dropdowns to explore relationships between different metrics:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <Typography component="li" variant="body2"><strong>Equipment Age vs Efficiency:</strong> Does older equipment improve more? (Answer: No significant correlation)</Typography>
            <Typography component="li" variant="body2"><strong>Capacity vs Savings:</strong> Do larger systems save more money? (Explore the data)</Typography>
            <Typography component="li" variant="body2"><strong>kW/Ton Before vs After:</strong> How do starting conditions affect outcomes?</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default TrendsAnalysis
